"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCurrency } from "@/lib/utils";
import { PaymentOverview } from "@/services/paymentService";

interface BillingSummaryCardsProps {
  totals: PaymentOverview;
}

/**
 * The billing summary.
 *
 * Every component of the invoiced total is shown, so the figures visibly add
 * up. Discounts in particular used to be invisible here while still being
 * netted out of the pending figure, which made Paid + Pending fall short of
 * Total Invoiced with no explanation on screen.
 */
export function BillingSummaryCards({ totals }: BillingSummaryCardsProps) {
  const {
    totalAmount,
    discountAmount,
    cancelledAmount,
    creditAmount,
    paidAmount,
    pendingAmount,
  } = totals;

  // Restated on screen so a future regression in the summary maths is obvious
  // rather than silent.
  const netReceivable =
    totalAmount - discountAmount - cancelledAmount + creditAmount;
  const settled = paidAmount + pendingAmount;
  const isBalanced = Math.abs(netReceivable - settled) < 0.005;

  return (
    <div className="space-y-2">
      <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-5">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">
              Total Invoiced
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(totalAmount)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Discounts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {formatCurrency(discountAmount)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Cancelled</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {formatCurrency(cancelledAmount)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Paid</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(paidAmount)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {formatCurrency(pendingAmount)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Credit is unusual -- only surface the card when there is one. */}
      {creditAmount > 0.005 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">
              Credit (owed to customers)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {formatCurrency(creditAmount)}
            </div>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-muted-foreground">
        {formatCurrency(totalAmount)} &minus; {formatCurrency(discountAmount)}{" "}
        discounts &minus; {formatCurrency(cancelledAmount)} cancelled
        {creditAmount > 0.005 ? (
          <> + {formatCurrency(creditAmount)} credit</>
        ) : null}{" "}
        = {formatCurrency(netReceivable)} = {formatCurrency(paidAmount)} paid +{" "}
        {formatCurrency(pendingAmount)} pending
        {isBalanced ? null : (
          <span className="ml-1 font-medium text-red-600">
            (does not reconcile)
          </span>
        )}
      </p>
    </div>
  );
}
